import React from 'react';
import {
  Container,
  FormField,
  Header,
  Input,
  Link,
  RadioGroup,
  Slider,
  SpaceBetween,
  Toggle,
  Popover
} from '@amzn/awsui-components-core';

interface TeamReminderSettingsProps {
  formState: any;
  errors: any;
  onInputChange: (field: string, value: any) => void;
  disabled?: boolean;
}

export const RemindersSettings: React.FC<TeamReminderSettingsProps> = ({
  formState,
  errors,
  onInputChange,
  disabled = false
}) => {
  return (
    <Container
      header={
        <Header
          description="Set up your reminders preferences to receive alerts and stay informed about your shift updates."
          variant="h2"
        >
          Reminders settings
        </Header>
      }
    >
      <SpaceBetween size="l">
        <FormField
          constraintText="Team name must be 2-50 characters long and can contain letters, numbers, spaces, and hyphens."
          description="Enter a name that identifies your team for reminder purposes."
          info={
            <Popover
              content="You can find your oncall team on the 'oncall.corp.amazon.com'. Don't confuse oncall team an LDAP group."
              dismissButton
              position="top"
              size="medium"
              triggerType="text"
            >
              <Link variant="info">Info</Link>
            </Popover>
          }
          label="Oncall schedule team name"
          errorText={errors.teamName}
        >
          <Input
            placeholder="my-awesome-team"
            value={formState.teamName}
            onChange={({ detail }) => onInputChange('teamName', detail.value)}
            disabled={disabled}
          />
        </FormField>

        <FormField
          description="Choose how you want to receive reminders from the system."
          label="Reminder method"
          stretch
        >
          <RadioGroup
            items={[
              {
                description: 'Receive reminders via text message to your mobile phone.',
                label: 'SMS',
                value: 'sms'
              },
              {
                description: 'Receive reminders through Telegram bot messages.',
                label: 'Telegram',
                value: 'telegram'
              }
            ]}
            value={formState.reminderMethod}
            onChange={({ detail }) => onInputChange('reminderMethod', detail.value)}
            readOnly={disabled}
          />
        </FormField>

        <FormField
          constraintText="Use international format starting with + followed by country code and phone number (e.g., +1234567890). Available when SMS is selected."
          description="Enter your mobile phone number in international format for SMS reminders."
          label="Mobile phone number"
          errorText={errors.phoneNumber}
        >
          <Input
            inputMode="tel"
            placeholder="+1234567890"
            value={formState.phoneNumber}
            onChange={({ detail }) => onInputChange('phoneNumber', detail.value)}
            disabled={disabled || formState.reminderMethod !== 'sms'}
          />
        </FormField>

        <FormField
          constraintText="Your numeric Telegram chat ID. You can get this by messaging @userinfobot on Telegram. Available when Telegram is selected."
          description="Enter your Telegram chat ID to receive reminders via Telegram bot."
          label="Telegram chat ID"
          errorText={errors.telegramChatId}
        >
          <Input
            inputMode="numeric"
            placeholder="123456789"
            value={formState.telegramChatId}
            onChange={({ detail }) => onInputChange('telegramChatId', detail.value)}
            disabled={disabled || formState.reminderMethod !== 'telegram'}
          />
        </FormField>

        <FormField
          constraintText="When this setting is disabled, you will receive reminders only at the start of your shift, not before."
          label="Enable Advance Reminders"
        >
          <Toggle
            checked={formState.remindersEnabled}
            onChange={({ detail }) => onInputChange('remindersEnabled', detail.checked)}
            disabled={disabled}
          >
            Receive advance reminders
          </Toggle>
        </FormField>

        <FormField
          constraintText="Choose a value between 1 hour and 12 hours. This setting is only active when reminders are enabled."
          description="Set what time before your shift you want to be notified."
          label="Reminder before"
          info={
            <Popover
              content="If your team's updates are more frequent than time selected - you will be notified straight away after the system gets the info about the shift."
              dismissButton
              position="top"
              size="medium"
              triggerType="text"
            >
              <Link variant="info">Info</Link>
            </Popover>
          }
        >
          <Slider
            max={12}
            min={1}
            referenceValues={[3,5,7,9,11]}
            step={1}
            tickMarks
            value={formState.remindersTime}
            onChange={({ detail }) => onInputChange('remindersTime', detail.value)}
            disabled={disabled || !formState.remindersEnabled}
          />
        </FormField>
      </SpaceBetween>
    </Container>
  );
};
